import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { X } from "lucide-react";

/**
 * A modal that renders a QR code for the given URL. Parents control open/close
 * via `open` + `onClose`. Rendered at the top of the DOM (via fixed positioning)
 * so it escapes any grid / overflow-hidden containers.
 */
export default function QrModal({ url, label, open, onClose }) {
  const [qrDataUrl, setQrDataUrl] = useState("");
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    QRCode.toDataURL(url, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 320,
      color: { dark: "#16171B", light: "#FBF5E9" },
    })
      .then((dataUrl) => {
        if (!cancelled) setQrDataUrl(dataUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, url]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => closeBtnRef.current?.focus(), 20);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Open on phone — ${label}`}
      onClick={onClose}
      className="fixed inset-0 z-[9999] bg-ink/85 backdrop-blur-sm flex items-center justify-center p-6"
      data-testid="phone-qr-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-paper-surface border-2 border-ink shadow-hard-md max-w-md w-full p-6 sm:p-8 text-center"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="text-left">
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">
              Scan to open on your phone
            </div>
            <div className="font-headline text-xl font-bold leading-tight mt-1">
              {label}
            </div>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-ink hover:text-press-red p-1"
            data-testid="phone-qr-close"
          >
            <X size={22} />
          </button>
        </div>

        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt={`QR code — ${label}`}
            className="mx-auto border-2 border-ink"
            width={320}
            height={320}
          />
        ) : (
          <div className="w-[320px] h-[320px] mx-auto border-2 border-dashed border-ink flex items-center justify-center font-mono text-xs text-ink-muted">
            Generating QR…
          </div>
        )}

        <div className="mt-4 font-body italic text-sm text-ink-secondary">
          Point your phone camera at the code. The link opens straight in the
          right app.
        </div>

        <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-ink-muted break-all">
          {url}
        </div>
      </div>
    </div>
  );
}
